import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import {
  MapPin,
  Bell,
  ChevronRight,
  SkipForward,
  Check,
  X,
  AlertCircle,
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { SEO } from '@/components/seo';
import { useCompletePermissions } from '@/api/mutations/useOnboardingMutations';
import { registerForPush } from '@/services/pushRegistration';

type PermissionStatus = 'prompt' | 'granted' | 'denied' | 'unavailable';

interface PermissionState {
  location: PermissionStatus;
  notifications: PermissionStatus;
}

/**
 * OnboardingPermissions page - Request location and notification permissions
 *
 * Handles both native (Capacitor) and web platforms with appropriate
 * permission request flows for each.
 */
export function OnboardingPermissions() {
  const navigate = useNavigate();
  const { mutate: completePermissions } = useCompletePermissions();

  const [permissions, setPermissions] = useState<PermissionState>({
    location: 'prompt',
    notifications: 'prompt',
  });
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [isRequestingNotifications, setIsRequestingNotifications] = useState(false);

  const isNative = Capacitor.isNativePlatform();

  // Check current permission status on mount
  useEffect(() => {
    checkPermissionStatus();
  }, []);

  const checkPermissionStatus = async () => {
    // Check location permission
    try {
      if (isNative) {
        const status = await Geolocation.checkPermissions();
        setPermissions((prev) => ({
          ...prev,
          location: mapNativePermission(status.location),
        }));
      } else if ('permissions' in navigator) {
        const status = await navigator.permissions.query({ name: 'geolocation' });
        setPermissions((prev) => ({
          ...prev,
          location: status.state as PermissionStatus,
        }));
      }
    } catch {
      // Geolocation not available
      setPermissions((prev) => ({ ...prev, location: 'unavailable' }));
    }

    // Check notification permission
    try {
      if (isNative) {
        // Passive read only — registerForPush() has no read-only mode, it
        // conditionally requests permission itself, which would prompt on
        // every mount if reused here instead of on the button tap below.
        const status = await FirebaseMessaging.checkPermissions();
        setPermissions((prev) => ({
          ...prev,
          notifications: mapNativePermission(status.receive),
        }));
      } else if ('Notification' in window) {
        // Web API returns 'default' instead of 'prompt'
        const webPermission = Notification.permission;
        setPermissions((prev) => ({
          ...prev,
          notifications: webPermission === 'default' ? 'prompt' : webPermission as PermissionStatus,
        }));
      } else {
        setPermissions((prev) => ({ ...prev, notifications: 'unavailable' }));
      }
    } catch {
      setPermissions((prev) => ({ ...prev, notifications: 'unavailable' }));
    }
  };

  const mapNativePermission = (status: string): PermissionStatus => {
    switch (status) {
      case 'granted':
        return 'granted';
      case 'denied':
        return 'denied';
      case 'prompt':
      case 'prompt-with-rationale':
        return 'prompt';
      default:
        return 'prompt';
    }
  };

  const requestLocationPermission = useCallback(async () => {
    setIsRequestingLocation(true);
    try {
      if (isNative) {
        const status = await Geolocation.requestPermissions();
        setPermissions((prev) => ({
          ...prev,
          location: mapNativePermission(status.location),
        }));
      } else {
        // Web: Request permission by attempting to get position
        await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 10000,
          });
        });
        setPermissions((prev) => ({ ...prev, location: 'granted' }));
      }
    } catch (error: any) {
      // User denied or error occurred
      if (error?.code === 1 || error?.message?.includes('denied')) {
        setPermissions((prev) => ({ ...prev, location: 'denied' }));
      }
    } finally {
      setIsRequestingLocation(false);
    }
  }, [isNative]);

  const requestNotificationPermission = useCallback(async () => {
    setIsRequestingNotifications(true);
    try {
      if (isNative) {
        // registerForPush() checks/requests permission and, once granted,
        // acquires + submits the token — single source of truth, no separate
        // requestPermissions() call here. Read the resulting status back
        // (passive, no second prompt) to drive this card's UI.
        await registerForPush();
        const status = await FirebaseMessaging.checkPermissions();
        setPermissions((prev) => ({
          ...prev,
          notifications: mapNativePermission(status.receive),
        }));
      } else if ('Notification' in window) {
        // Web: Request notification permission
        const result = await Notification.requestPermission();
        setPermissions((prev) => ({
          ...prev,
          notifications: result as PermissionStatus,
        }));
      }
    } catch {
      setPermissions((prev) => ({ ...prev, notifications: 'denied' }));
    } finally {
      setIsRequestingNotifications(false);
    }
  }, [isNative]);

  const handleContinue = () => {
    // Mark permissions step as complete
    completePermissions(undefined, {
      onSettled: () => {
        // Navigate to profile setup regardless of API result
        navigate({ to: '/onboarding/profile' });
      },
    });
  };

  const handleSkip = () => {
    // Mark permissions step as complete even when skipping
    completePermissions(undefined, {
      onSettled: () => {
        navigate({ to: '/onboarding/profile' });
      },
    });
  };

  const getStatusIcon = (status: PermissionStatus) => {
    switch (status) {
      case 'granted':
        return <Icon as={Check} color="green.400" boxSize={5} />;
      case 'denied':
        return <Icon as={X} color="red.400" boxSize={5} />;
      case 'unavailable':
        return <Icon as={AlertCircle} color="gray.500" boxSize={5} />;
      default:
        return null;
    }
  };

  const getButtonText = (status: PermissionStatus, type: 'location' | 'notifications') => {
    const labels = {
      location: { prompt: 'Enable Location', granted: 'Enabled', denied: 'Denied' },
      notifications: { prompt: 'Enable Notifications', granted: 'Enabled', denied: 'Denied' },
    };

    if (status === 'unavailable') return 'Not Available';
    return labels[type][status] || labels[type].prompt;
  };

  const allPermissionsHandled =
    permissions.location !== 'prompt' && permissions.notifications !== 'prompt';

  return (
    <Box
      minH="100vh"
      color="white"
      display="flex"
      flexDirection="column"
    >
      <SEO title="Enable Permissions" />

      {/* Skip button — pt includes safe-area inset for Dynamic Island / notch */}
      <Flex
        justify="flex-end"
        px={4}
        pb={4}
        pt="calc(16px + env(safe-area-inset-top))"
      >
        <Button
          variant="ghost"
          color="var(--canvas-text-2)"
          rightIcon={<SkipForward size={16} />}
          onClick={handleSkip}
          _hover={{ color: 'var(--canvas-text)' }}
          data-testid="permissions-skip"
        >
          Skip
        </Button>
      </Flex>

      <Flex
        direction="column"
        align="center"
        justify="center"
        flex="1"
        p={8}
        textAlign="center"
      >
        <VStack spacing={8} maxW="400px" width="100%">
          {/* Header */}
          <VStack spacing={2}>
            <Heading size="lg" color="white">
              Enable Permissions
            </Heading>
            <Text fontSize="md" color="var(--canvas-text-2)">
              Get the most out of Paire with these permissions
            </Text>
          </VStack>

          {/* Permission Cards */}
          <VStack spacing={4} width="100%">
            {/* Location Permission */}
            <Box
              width="100%"
              bg="whiteAlpha.100"
              borderRadius="xl"
              p={5}
              border="1px solid"
              borderColor={
                permissions.location === 'granted'
                  ? 'green.500'
                  : permissions.location === 'denied'
                    ? 'red.500'
                    : 'whiteAlpha.200'
              }
            >
              <HStack spacing={4} align="flex-start">
                <Box
                  p={3}
                  bg="purple.500"
                  borderRadius="lg"
                  flexShrink={0}
                >
                  <Icon as={MapPin} boxSize={6} color="white" />
                </Box>
                <VStack align="flex-start" flex={1} spacing={1}>
                  <HStack>
                    <Text fontWeight="semibold" color="white">
                      Location
                    </Text>
                    {getStatusIcon(permissions.location)}
                  </HStack>
                  <Text fontSize="sm" color="var(--canvas-text-2)" textAlign="left">
                    Find restaurants nearby and get personalized recommendations
                  </Text>
                  <Button
                    size="sm"
                    mt={2}
                    bg={permissions.location === 'granted' ? 'green.500' : 'purple.500'}
                    color="white"
                    isDisabled={permissions.location !== 'prompt'}
                    isLoading={isRequestingLocation}
                    onClick={requestLocationPermission}
                    _hover={{
                      bg: permissions.location === 'granted' ? 'green.600' : 'purple.600',
                    }}
                    _disabled={{
                      opacity: 0.7,
                      cursor: 'not-allowed',
                    }}
                    data-testid="permission-btn-location"
                  >
                    {getButtonText(permissions.location, 'location')}
                  </Button>
                </VStack>
              </HStack>
            </Box>

            {/* Notifications Permission */}
            <Box
              width="100%"
              bg="whiteAlpha.100"
              borderRadius="xl"
              p={5}
              border="1px solid"
              borderColor={
                permissions.notifications === 'granted'
                  ? 'green.500'
                  : permissions.notifications === 'denied'
                    ? 'red.500'
                    : 'whiteAlpha.200'
              }
            >
              <HStack spacing={4} align="flex-start">
                <Box
                  p={3}
                  bg="orange.500"
                  borderRadius="lg"
                  flexShrink={0}
                >
                  <Icon as={Bell} boxSize={6} color="white" />
                </Box>
                <VStack align="flex-start" flex={1} spacing={1}>
                  <HStack>
                    <Text fontWeight="semibold" color="white">
                      Notifications
                    </Text>
                    {getStatusIcon(permissions.notifications)}
                  </HStack>
                  <Text fontSize="sm" color="var(--canvas-text-2)" textAlign="left">
                    Get updates on friend activity and restaurant deals
                  </Text>
                  <Button
                    size="sm"
                    mt={2}
                    bg={permissions.notifications === 'granted' ? 'green.500' : 'orange.500'}
                    color="white"
                    isDisabled={permissions.notifications !== 'prompt'}
                    isLoading={isRequestingNotifications}
                    onClick={requestNotificationPermission}
                    _hover={{
                      bg: permissions.notifications === 'granted' ? 'green.600' : 'orange.600',
                    }}
                    _disabled={{
                      opacity: 0.7,
                      cursor: 'not-allowed',
                    }}
                    data-testid="permission-btn-notifications"
                  >
                    {getButtonText(permissions.notifications, 'notifications')}
                  </Button>
                </VStack>
              </HStack>
            </Box>
          </VStack>

          {/* Info Text */}
          <Text fontSize="sm" color="var(--canvas-text-2)" px={4}>
            You can always change these permissions later in your device settings.
          </Text>

          {/* Continue Button */}
          <Button
            size="lg"
            width="100%"
            bg="purple.500"
            color="white"
            _hover={{ bg: 'purple.600' }}
            onClick={handleContinue}
            rightIcon={<ChevronRight size={20} />}
            opacity={allPermissionsHandled ? 1 : 0.8}
            data-testid="permissions-continue"
          >
            Continue
          </Button>
        </VStack>
      </Flex>
    </Box>
  );
}

export default OnboardingPermissions;
